import { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../lib/dates';
import { useCampus } from '../lib/useCampus';
import { api, asList } from '../lib/api';
import { sportsApi, housesApi, type SportsItem, type SportEvent, type House } from '../lib/leadershipApi';

export function SportsPage() {
  const [tab, setTab] = useState<'inventory' | 'events'>('inventory');
  const [instId, setInstId] = useState<string | null>(null);
  /* ⚠ Was: fetch one student and read their campus — "whichever campus the first student row
     happens to belong to". Arbitrary on a multi-campus trust. See lib/useCampus.ts. */
  const { institutionId: campusId } = useCampus();
  useEffect(() => { setInstId(campusId); }, [campusId]);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Facilities</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Sports</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>Equipment inventory and events. Inter-house event results feed the house leaderboard.</p>
      </div>
      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <button className={tab === 'inventory' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('inventory')}>Equipment</button>
        <button className={tab === 'events' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('events')}>Events</button>
      </div>
      {tab === 'inventory' ? <SportsInventory instId={instId} /> : <SportsEvents instId={instId} />}
    </div>
  );
}

function SportsInventory({ instId }: { instId: string | null }) {
  const [items, setItems] = useState<SportsItem[]>([]);
  const [name, setName] = useState(''); const [cat, setCat] = useState(''); const [qty, setQty] = useState('');
  const load = useCallback(() => { sportsApi.items().then((r) => setItems(asList(r))).catch(() => setItems([])); }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!name.trim() || !instId) return;
    await sportsApi.addItem({ institutionId: instId, name: name.trim(), category: cat.trim() || null, quantity: Number(qty) || 0, notes: null });
    setName(''); setCat(''); setQty(''); load();
  };
  const restock = async (id: string) => { const by = Number(window.prompt('Add how many?')); if (by > 0) { await sportsApi.restock(id, by); load(); } };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input className="input" placeholder="Item (e.g. Football)" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: '2 1 180px' }} />
        <input className="input" placeholder="Category" value={cat} onChange={(e) => setCat(e.target.value)} style={{ width: 120 }} />
        <input className="input" type="number" placeholder="Qty" value={qty} onChange={(e) => setQty(e.target.value)} style={{ width: 80 }} />
        <button className="btn btn-accent" disabled={!instId} onClick={add}>Add</button>
      </div>
      {items.length === 0 ? <div style={{ color: 'var(--ink-3)', padding: 12 }}>No equipment yet.</div>
        : items.map((i) => (
          <div key={i.id} className="card" style={{ padding: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}><span style={{ fontWeight: 600 }}>{i.name}</span>{i.category && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}> · {i.category}</span>}</div>
            <span style={{ fontWeight: 700, color: i.quantity === 0 ? 'var(--brand,#c1272d)' : 'inherit' }}>{i.quantity}</span>
            <button className="btn" style={{ padding: '2px 10px' }} onClick={() => restock(i.id)}>+ Restock</button>
            <button className="btn" style={{ padding: '2px 8px' }} onClick={async () => { if (window.confirm('Remove?')) { await sportsApi.deleteItem(i.id); load(); } }}>✕</button>
          </div>
        ))}
    </div>
  );
}

function SportsEvents({ instId }: { instId: string | null }) {
  const [events, setEvents] = useState<SportEvent[]>([]);
  const [houses, setHouses] = useState<House[]>([]);
  const [name, setName] = useState(''); const [sport, setSport] = useState(''); const [date, setDate] = useState(''); const [inter, setInter] = useState(true);
  const [resultFor, setResultFor] = useState<string | null>(null);

  const load = useCallback(() => { sportsApi.events().then((r) => setEvents(asList(r))).catch(() => setEvents([])); }, []);
  useEffect(load, [load]);
  // ⚠ The leaderboard now returns a season's BOARD rather than a bare array. This screen only
  // wants the houses, to pick one when recording a result.
  useEffect(() => { housesApi.leaderboard().then((b) => setHouses(b.houses)).catch(() => setHouses([])); }, []);

  const create = async () => {
    if (!name.trim() || !date || !instId) return;
    await sportsApi.createEvent({ institutionId: instId, name: name.trim(), sport: sport.trim() || null, date, isInterHouse: inter });
    setName(''); setSport(''); setDate(''); load();
  };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <input className="input" placeholder="Event (e.g. Annual Sports Day)" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: '2 1 200px' }} />
          <input className="input" placeholder="Sport" value={sport} onChange={(e) => setSport(e.target.value)} style={{ width: 120 }} />
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8 }}>
          <input type="checkbox" checked={inter} onChange={(e) => setInter(e.target.checked)} /> Inter-house (results award house points)
        </label>
        <button className="btn btn-accent" disabled={!instId} onClick={create}>Add event</button>
      </div>

      {events.map((ev) => (
        <div key={ev.id} className="card" style={{ padding: 14, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{ev.name}</span>
              {ev.sport && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}> · {ev.sport}</span>}
              {ev.isInterHouse && <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>Inter-house</span>}
            </div>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{formatDate(ev.date)}</span>
            <button className="btn" onClick={() => setResultFor(resultFor === ev.id ? null : ev.id)}>+ Result</button>
          </div>
          {ev.results.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {ev.results.map((r, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--ink-2)', padding: '3px 0' }}>
                  #{r.position} {r.label ?? ''} {r.studentName ? `· ${r.studentName}` : ''} {r.housePoints > 0 ? `· +${r.housePoints} pts` : ''}
                </div>
              ))}
            </div>
          )}
          {resultFor === ev.id && <ResultForm eventId={ev.id} houses={houses} onDone={() => { setResultFor(null); load(); }} />}
        </div>
      ))}
    </div>
  );
}

function ResultForm({ eventId, houses, onDone }: { eventId: string; houses: House[]; onDone: () => void }) {
  const [position, setPosition] = useState('1'); const [label, setLabel] = useState('');
  const [houseId, setHouseId] = useState(''); const [points, setPoints] = useState('10');
  const [student, setStudent] = useState<{ id: string; fullName: string } | null>(null);
  const [search, setSearch] = useState(''); const [results, setResults] = useState<Array<{ id: string; fullName: string; admissionNumber: string }>>([]);

  const doSearch = async () => { if (search.trim().length < 2) return; try { setResults(asList(await api.get(`/api/students?search=${encodeURIComponent(search.trim())}&take=6`))); } catch { setResults([]); } };
  const save = async () => {
    await sportsApi.recordResult({ eventId, position: Number(position) || 1, label: label.trim() || null, houseId: houseId || null, studentId: student?.id ?? null, studentName: student?.fullName ?? null, housePoints: Number(points) || 0 });
    onDone();
  };

  return (
    <div className="card" style={{ padding: 12, marginTop: 10, background: 'var(--surface-2,#f6f6f4)' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <input className="input" type="number" placeholder="Pos" value={position} onChange={(e) => setPosition(e.target.value)} style={{ width: 70 }} />
        <input className="input" placeholder="Event/category (e.g. 100m U-14)" value={label} onChange={(e) => setLabel(e.target.value)} style={{ flex: '1 1 160px' }} />
        <select className="input" value={houseId} onChange={(e) => setHouseId(e.target.value)}>
          <option value="">No house</option>
          {houses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <input className="input" type="number" placeholder="Pts" value={points} onChange={(e) => setPoints(e.target.value)} style={{ width: 70 }} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {student ? <div style={{ flex: 1 }}><strong>{student.fullName}</strong> <button className="btn" style={{ padding: '2px 8px' }} onClick={() => setStudent(null)}>Change</button></div>
          : <><input className="input" placeholder="Student (optional)" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()} /><button className="btn" onClick={doSearch}>Find</button></>}
      </div>
      {!student && results.map((s) => <button key={s.id} className="btn" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }} onClick={() => { setStudent({ id: s.id, fullName: s.fullName }); setResults([]); }}>{s.fullName} · {s.admissionNumber}</button>)}
      <button className="btn btn-accent" onClick={save}>Save result</button>
    </div>
  );
}
